import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon, CurrencyDollarIcon, ShoppingBagIcon, ShoppingCartIcon, TruckIcon,
  PhoneIcon, FilmIcon, WifiIcon, GiftIcon, AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { formatCurrencyInput, parseCurrencyInput } from '../utils/currencyUtils';
const categories = [
  { id: 'food', label: 'Makan', icon: ShoppingBagIcon },
  { id: 'transport', label: 'Transport', icon: TruckIcon },
  { id: 'groceries', label: 'Belanja', icon: ShoppingCartIcon },
  { id: 'entertainment', label: 'Hiburan', icon: FilmIcon },
  { id: 'internet', label: 'Internet', icon: WifiIcon },
  { id: 'other', label: 'Lainnya', icon: GiftIcon }
];

const AddBillModal = ({ members, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    paid_by_member_id: members[0]?.id || '',
    split_member_ids: members.map((m) => m.id),
    category: 'food'
  });
  const [splitType, setSplitType] = useState('equal'); // 'equal' | 'custom'
  const [customAmounts, setCustomAmounts] = useState({});
  const [loading, setLoading] = useState(false);

  const toggleMember = (memberId) => {
    setFormData((prev) => {
      const newIds = prev.split_member_ids.includes(memberId)
        ? prev.split_member_ids.filter((id) => id !== memberId)
        : [...prev.split_member_ids, memberId];
      return { ...prev, split_member_ids: newIds };
    });
    // Remove custom amount if member deselected
    if (customAmounts[memberId]) {
      setCustomAmounts((prev) => {
        const next = { ...prev };
        delete next[memberId];
        return next;
      });
    }
  };

  const handleCustomAmountChange = (memberId, value) => {
    setCustomAmounts((prev) => ({ ...prev, [memberId]: value }));
  };

  const totalCustom = Object.values(customAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const remaining = formData.amount ? parseFloat(formData.amount) - totalCustom : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount || formData.split_member_ids.length === 0) return;

    setLoading(true);
    try {
      const payload = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        category: formData.category,
        paid_by_member_id: parseInt(formData.paid_by_member_id),
        split_member_ids: formData.split_member_ids
      };

      if (splitType === 'custom') {
        const amounts = {};
        formData.split_member_ids.forEach((mid) => {
          amounts[mid] = parseFloat(customAmounts[mid]) || 0;
        });
        payload.split_amounts = amounts;
      }

      await onSubmit(payload);
    } finally {
      setLoading(false);
    }
  };

  const equalSplit = formData.amount && formData.split_member_ids.length > 0
    ? parseFloat(formData.amount) / formData.split_member_ids.length : 0;

  const fmt = (n) => n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white rounded-t-[32px] sm:rounded-3xl p-6 w-full max-w-md shadow-[0_-10px_40px_rgba(0,0,0,0.1)] max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}>

          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-6 sm:hidden"></div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Tambah Tagihan</h3>
            <button onClick={onClose} data-testid="close-add-bill-modal"
              className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors">
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Description */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Deskripsi</label>
              <input type="text" data-testid="bill-description-input" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Misal: Makan Bakso, Bensin..."
                className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" />
            </div>

            {/* Amount */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Nominal</label>
              <div className="relative">
                <CurrencyDollarIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input type="text" inputMode="numeric" data-testid="bill-amount-input" value={formatCurrencyInput(formData.amount)}
                  onChange={(e) => setFormData({ ...formData, amount: parseCurrencyInput(e.target.value) })}
                  placeholder="Masukkan nominal..."
                  className="w-full pl-12 pr-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Kategori</label>
              <div className="grid grid-cols-3 gap-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button key={cat.id} type="button" data-testid={`category-${cat.id}`}
                      onClick={() => setFormData({ ...formData, category: cat.id })}
                      className={`flex flex-col items-center gap-1 py-3 rounded-[16px] border transition-all ${
                        formData.category === cat.id ? 'border-primary bg-primary/5 text-primary' : 'border-border text-slate-600 hover:border-slate-300'
                      }`}>
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Paid By */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Dibayar oleh</label>
              <select data-testid="bill-paid-by-select" value={formData.paid_by_member_id}
                onChange={(e) => setFormData({ ...formData, paid_by_member_id: e.target.value })}
                className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all bg-white appearance-none">
                {members.map((member) => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>

            {/* Split Type Toggle */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Metode Bagi</label>
              <div className="flex gap-2 bg-surface p-1 rounded-full">
                <button type="button" onClick={() => setSplitType('equal')} data-testid="split-type-equal"
                  className={`flex-1 py-2 px-3 rounded-full font-semibold text-sm transition-all ${
                    splitType === 'equal' ? 'bg-primary text-white shadow-sm' : 'text-slate-600'
                  }`}>
                  Bagi Rata
                </button>
                <button type="button" onClick={() => setSplitType('custom')} data-testid="split-type-custom"
                  className={`flex-1 py-2 px-3 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-1 ${
                    splitType === 'custom' ? 'bg-primary text-white shadow-sm' : 'text-slate-600'
                  }`}>
                  <AdjustmentsHorizontalIcon className="w-4 h-4" />
                  Custom
                </button>
              </div>
            </div>

            {/* Member Selection */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Dibagi ke</label>
              <div className="flex flex-wrap gap-2">
                {members.map((member) => (
                  <button key={member.id} type="button" data-testid={`split-member-${member.id}`}
                    onClick={() => toggleMember(member.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                      formData.split_member_ids.includes(member.id) ? 'border-primary bg-primary text-white' : 'border-border text-slate-600 hover:border-slate-300'
                    }`}>
                    <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${member.name}`} alt={member.name} className="w-5 h-5 rounded-full" />
                    {member.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount Inputs */}
            {splitType === 'custom' && formData.amount && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-3">
                <p className="text-xs text-slate-500">Masukkan nominal untuk setiap orang:</p>
                {members.filter((m) => formData.split_member_ids.includes(m.id)).map((member) => (
                  <div key={member.id} className="flex items-center gap-3">
                    <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${member.name}`} alt={member.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700 w-20 truncate">{member.name}</span>
                    <div className="relative flex-1">
                      <span className="text-xs text-slate-400 absolute left-3 top-1/2 -translate-y-1/2">Rp</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        data-testid={`custom-amount-${member.id}`}
                        value={formatCurrencyInput(customAmounts[member.id])}
                        onChange={(e) => handleCustomAmountChange(member.id, parseCurrencyInput(e.target.value))}
                        placeholder="0"
                        className="w-full pl-10 pr-3 py-2 rounded-[12px] border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
                      />
                    </div>
                  </div>
                ))}
                <div className={`flex items-center justify-between p-3 rounded-[12px] text-sm font-semibold ${
                  Math.abs(remaining) < 1 ? 'bg-success-bg text-success-text' : 'bg-danger-bg text-danger-text'
                }`}>
                  <span>Sisa:</span>
                  <span data-testid="custom-split-remaining">Rp {fmt(remaining)}</span>
                </div>
              </motion.div>
            )}

            {/* Equal Split Preview */}
            {splitType === 'equal' && equalSplit > 0 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-surface p-4 rounded-[16px] text-center">
                <p className="text-sm text-slate-600 mb-1">Per orang bayar:</p>
                <p className="text-2xl font-bold text-primary" data-testid="bill-split-preview">
                  Rp {fmt(equalSplit)}
                </p>
              </motion.div>
            )}

            <button type="submit" data-testid="submit-add-bill"
              disabled={!formData.description || !formData.amount || formData.split_member_ids.length === 0 || loading ||
                (splitType === 'custom' && Math.abs(remaining) >= 1)}
              className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Menyimpan...' : 'Simpan Tagihan'}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddBillModal;
